import { useState, useMemo } from 'react'
import { toast } from 'react-toastify'

import FbButton from 'components/FbUI/FbButton'
import { Modal } from 'components/UI/Modal'
import {
  type SenderEmail,
  useGetWorkspaceSenderEmails,
  useSyncWorkspaceSenderEmails,
  useAttachCampaignSenderEmails,
  useGetCampaignSenderEmails,
} from '../sender_emails_api'

const STATUS_LABELS: Record<string, string> = {
  connected: 'Connected',
  not_connected: 'Not Connected',
  pending_move: 'Pending Move',
  pending_deletion: 'Pending Deletion',
}

const STATUS_COLORS: Record<string, string> = {
  connected: 'bg-green-100 text-green-800',
  not_connected: 'bg-gray-100 text-gray-800',
  pending_move: 'bg-amber-100 text-amber-800',
  pending_deletion: 'bg-red-100 text-red-800',
}

interface AddSenderEmailsModalProps {
  isOpen: boolean
  onClose: () => void
  bisonCampaignId: number
}

export function AddSenderEmailsModal({
  isOpen,
  onClose,
  bisonCampaignId,
}: AddSenderEmailsModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const { data: workspaceSenderEmails, isLoading: isLoadingWorkspace } =
    useGetWorkspaceSenderEmails()
  const { data: campaignSenderEmails } =
    useGetCampaignSenderEmails(bisonCampaignId)

  const syncMutation = useSyncWorkspaceSenderEmails()
  const attachMutation = useAttachCampaignSenderEmails(bisonCampaignId)

  // Filter out sender emails already attached to the campaign
  const availableSenderEmails = useMemo(() => {
    if (!workspaceSenderEmails?.results) return []
    const attachedIds = new Set(
      campaignSenderEmails?.results?.map((e) => e.id) ?? []
    )
    return workspaceSenderEmails.results.filter(
      (email) => !attachedIds.has(email.id)
    )
  }, [workspaceSenderEmails, campaignSenderEmails])

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced: ${result.created} added, ${result.updated} updated`
      )
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Failed to sync sender emails'
      toast.error(errorMessage)
    }
  }

  const handleToggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedIds.size === availableSenderEmails.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(availableSenderEmails.map((e) => e.id)))
    }
  }

  const handleAttach = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Please select at least one sender email')
      return
    }

    try {
      const result = await attachMutation.mutateAsync({
        sender_email_ids: Array.from(selectedIds),
      })

      toast.success(result.message || 'Sender emails updated successfully')
      if (result.skipped_count > 0) {
        toast.info(`Skipped ${result.skipped_count} sender email(s)`)
      }

      setSelectedIds(new Set())
      onClose()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Failed to attach sender emails'
      toast.error(errorMessage)
    }
  }

  const handleClose = () => {
    setSelectedIds(new Set())
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Sender Emails</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {/* Header actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-fg-secondary">
              {availableSenderEmails.length} sender email(s) available
            </div>
            <div className="flex gap-2">
              <FbButton
                onClick={handleSync}
                disabled={syncMutation.isPending}
                loading={syncMutation.isPending}
                variant="secondary"
                size="sm"
              >
                Sync Workspace
              </FbButton>
              <FbButton
                onClick={handleSelectAll}
                variant="secondary"
                size="sm"
                disabled={availableSenderEmails.length === 0}
              >
                {selectedIds.size === availableSenderEmails.length
                  ? 'Deselect All'
                  : 'Select All'}
              </FbButton>
            </div>
          </div>

          {/* Loading state */}
          {isLoadingWorkspace && (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading sender emails...</div>
            </div>
          )}

          {/* Empty state */}
          {!isLoadingWorkspace && availableSenderEmails.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-center max-w-md">
                <h3 className="text-lg font-semibold text-fg mb-2">
                  No sender emails available
                </h3>
                <p className="text-sm text-fg-secondary mb-4">
                  All workspace sender emails are already attached to this
                  campaign, or you have not synced your workspace yet.
                </p>
              </div>
            </div>
          )}

          {/* Sender email list */}
          {!isLoadingWorkspace && availableSenderEmails.length > 0 && (
            <div className="max-h-[400px] overflow-y-auto border rounded-lg divide-y">
              {availableSenderEmails.map((email) => (
                <SenderEmailRow
                  key={email.id}
                  email={email}
                  isSelected={selectedIds.has(email.id)}
                  onToggle={() => handleToggleSelect(email.id)}
                />
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <FbButton variant="secondary" onClick={handleClose}>
          Cancel
        </FbButton>
        <FbButton
          variant="primary"
          onClick={handleAttach}
          disabled={selectedIds.size === 0 || attachMutation.isPending}
          loading={attachMutation.isPending}
        >
          Attach {selectedIds.size > 0 && `(${selectedIds.size})`}
        </FbButton>
      </Modal.Footer>
    </Modal>
  )
}

interface SenderEmailRowProps {
  email: SenderEmail
  isSelected: boolean
  onToggle: () => void
}

function SenderEmailRow({ email, isSelected, onToggle }: SenderEmailRowProps) {
  return (
    <div
      className={`flex items-center gap-4 p-3 cursor-pointer transition-colors ${
        isSelected ? 'bg-brand/5 hover:bg-brand/5' : 'hover:bg-brand/5'
      }`}
      onClick={onToggle}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        className="h-4 w-4 cursor-pointer rounded border-line-form text-brand accent-brand focus:ring-1 focus:ring-brand"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-fg truncate">{email.name}</p>
        <p className="text-sm text-fg-secondary">{email.email}</p>
      </div>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
          STATUS_COLORS[email.status] || STATUS_COLORS.not_connected
        }`}
      >
        {STATUS_LABELS[email.status] || email.status}
      </span>
      <span className="text-sm text-fg-secondary">
        Daily: {email.daily_limit}
      </span>
    </div>
  )
}
